import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { personKeys, userKeys } from "@/lib/query";
import type { AppRole } from "@/schemas/users";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  banned: boolean;
  banReason: string | null;
  banExpires: string | null;
  createdAt: string;
  /**
   * The employee this account signs in as. Null means nothing is linked —
   * which leaves everything "my"-shaped (My Dashboard, My Certifications,
   * own time off) empty for whoever uses it.
   */
  personId: string | null;
  personName: string | null;
}

export type { User };

interface UsersListResponse {
  data: User[];
  /** Every account, ignoring the tier filter — for the hidden-rows note. */
  totalAll: number;
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

// Reads go through the app-standard cursor-paginated /api/users endpoint
// (name-or-email search + column sort); mutations stay on Better Auth's
// admin.* plugin methods.
export function useUsers(
  filters: { search?: string; sort?: string; dir?: "asc" | "desc"; roles?: string[] },
  pageRequest: PageRequest,
) {
  return useQuery({
    queryKey: [...userKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<UsersListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.roles?.length) params.set("roles", filters.roles.join(","));
      if (filters.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const res = await fetch(`/api/users?${params}`);
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; email: string; password: string; role: AppRole }) => {
      const { error } = await authClient.admin.createUser(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create user");
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; role: AppRole }) => {
      const { error } = await authClient.admin.setRole(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to change role");
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.removeUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to delete user");
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const { error } = await authClient.admin.setUserPassword(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Password reset");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reset password");
    },
  });
}

/**
 * Set or clear which employee an account belongs to.
 *
 * Not a Better Auth admin method like its neighbours — the link lives on the
 * PERSON row, not the account, so it goes through our own route.
 *
 * Invalidates people as well as accounts: the link is part of the person
 * record, shows in their change history, and decides whether their profile
 * offers "View as this person".
 */
export function useSetAccountPerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; personId: string | null }) => {
      const res = await fetch(`/api/users/${input.userId}/person`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ personId: input.personId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Failed to update the link");
      }
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      toast.success(input.personId ? "Account linked" : "Account unlinked");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to update the link");
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; data: { name?: string } }) => {
      const { error } = await authClient.admin.updateUser(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to update user");
    },
  });
}

export function useBanUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; banReason?: string }) => {
      const { error } = await authClient.admin.banUser(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User banned");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to ban user");
    },
  });
}

export function useUnbanUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.unbanUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User unbanned");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to unban user");
    },
  });
}
